import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import SlidePanel from '../shared/SlidePanel';
import { capabilityGroupsFor, fetchRoles, createRole, saveRole, resetRole, deleteRole, errorText } from './rolesStore';
import { loadAccess } from './access';
import BaileyTeam from './BaileyTeam';
import BelowMenuRule from './BelowMenuRule';
import '../settings/SettingsModern.css';
import './permissions.css';

/**
 * Team > Permissions: the roles and what each can do. Built-in roles are
 * editable (except Owner) and can be reset; "New role" makes a custom one.
 * Assigning a role to a person happens on their profile, not here.
 *
 * The server decides who may change what (docs/permissions.md §5): this page
 * only greys out what it already knows you can't grant.
 */
export default function PermissionsPage() {
  const [state, setState] = useState({ loading: true, error: null, roles: [], ownerOnly: [], canManage: false, groupLocked: false, grantable: [], scope: null });
  const [editing, setEditing] = useState(null);
  const history = useHistory();
  // The Front desk is set up in ONE place, Settings › Reception: its row
  // here only points there (no second editor for the same thing).
  const openRole = (r) => {
    if (r.frontDesk) { history.push('/settings/reception-mode'); return; }
    setEditing(r);
  };

  const load = useCallback(async () => {
    try {
      const r = await fetchRoles();
      setState({ loading: false, error: null, ...r });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: errorText(e, 'Could not load roles.') }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onNew = () => {
    const from = state.roles.find((r) => r.key === 'standard');
    setEditing({ key: null, name: '', description: '', caps: from ? from.caps.slice() : [], custom: true, isNew: true });
  };
  const totalFor = (r) => capabilityGroupsFor(r).reduce((n, g) => n + g.caps.length, 0);
  // `catalogue` is derived by the server (any of the "Your menu" caps): never counted or shown.
  const shownCaps = (r) => (r.caps || []).filter((c) => c !== 'catalogue');

  return (
    <div className="catalogue-content-wrapper perm-page">
      <div className="settings-card">
        <div className="settings-card-header perm-card-header">
          <div>
            <h2>Roles</h2>
            <p>What each role can see and do. Give someone a role from their profile in Team Members.{state.scope && state.scope.sites > 1 ? ' Roles are shared by all your locations.' : ''}</p>
          </div>
          {state.canManage && <button type="button" className="bu-inline-save" onClick={onNew}>New role</button>}
        </div>
        {state.loading && <p className="perm-intro perm-loading">Loading roles…</p>}
        {state.error && <p className="perm-intro perm-error">{state.error} <button type="button" className="acc-edit" onClick={load}>Try again</button></p>}
        {!state.loading && !state.error && (
          <div className="perm-list">
            {state.roles.map((r) => (
              <button key={r.key} type="button" className="perm-row" onClick={() => openRole(r)}>
                <span className="perm-row-text">
                  <span className="perm-row-name">
                    {r.name}
                    {r.locked && <span className="settings-badge settings-badge-default">Locked</span>}
                    {r.custom && <span className="settings-badge settings-badge-info">Custom</span>}
                    {r.edited && !r.frontDesk && <span className="settings-badge settings-badge-info">Edited</span>}
                    {r.frontDesk && <span className="settings-badge settings-badge-default">Reception mode</span>}
                  </span>
                  <span className="perm-row-desc">{r.frontDesk ? 'What a locked front-desk device shows. Set up in Settings › Reception.' : r.description}</span>
                </span>
                <span className="perm-row-meta">
                  {!r.frontDesk && <span className="perm-row-count">{r.owner ? 'Everything' : !shownCaps(r).length ? 'Own work only' : `${shownCaps(r).length} of ${totalFor(r)}`}</span>}
                  {r.people ? <span className="perm-row-people">{r.people} {r.people === 1 ? 'person' : 'people'}</span> : null}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <BelowMenuRule />

      <BaileyTeam />

      <div className="settings-card">
        <div className="settings-card-header">
          <h2>Roles and consent are different things</h2>
          <p>A role is what someone can do for the salon, whether they're employed or self-employed. A self-employed stylist's own bookings, clients and money are always theirs, and their private clients stay private whatever role they have. What the salon may do with their clients, such as marketing or loyalty, is agreed by them under Marketing and Loyalty.</p>
          <p className="perm-aside">Reception mode locks one shared front-desk device with a PIN, and you choose what it shows in Settings › Reception. For a desk that's staffed by one person, give them the Receptionist role instead.</p>
        </div>
      </div>

      {editing && (
        <RoleEditor
          role={editing}
          canManage={state.canManage}
          groupLocked={state.groupLocked}
          ownerOnly={state.ownerOnly}
          grantable={state.grantable}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); loadAccess(); }}
        />
      )}
    </div>
  );
}

function RoleEditor({ role, canManage, groupLocked, ownerOnly, grantable, onClose, onSaved }) {
  const [v, setV] = useState({ ...role, caps: (role.caps || []).filter((c) => c !== 'catalogue') });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'delete' | 'reset'
  const readOnly = !!role.locked || !canManage;
  const groups = capabilityGroupsFor(role);
  const mine = new Set(grantable || []);
  // You can only change a role that can't do anything you can't.
  const outOfReach = !role.owner && (role.caps || []).some((c) => c !== 'catalogue' && !mine.has(c));
  const locked = readOnly || outOfReach;

  const capState = (id) => {
    if (role.owner) return { on: true, disabled: true, note: null };
    if ((ownerOnly || []).includes(id)) return { on: false, disabled: true, note: 'Owner only for now' };
    if (!locked && !mine.has(id) && !v.caps.includes(id)) return { on: false, disabled: true, note: 'You can\'t give this' };
    return { on: v.caps.includes(id), disabled: locked, note: null };
  };
  const toggle = (id) => {
    const s = capState(id);
    if (s.disabled) return;
    const order = groups.flatMap((g) => g.caps.map((c) => c.id));
    setV((x) => ({ ...x, caps: x.caps.includes(id) ? x.caps.filter((c) => c !== id) : order.filter((c) => x.caps.includes(c) || c === id) }));
  };
  const run = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); await onSaved(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const save = () => {
    const name = (v.name || '').trim();
    if (!name) { setError('Give the role a name.'); return; }
    return run(() => (role.isNew ? createRole({ ...v, name }) : saveRole({ ...v, name })));
  };

  const title = role.isNew ? 'New role' : readOnly ? role.name : `Edit ${role.name}`;
  let footer;
  if (confirm === 'delete') {
    footer = (<>
      <button type="button" className="sp-btn-secondary" onClick={() => setConfirm(null)} disabled={busy}>Keep role</button>
      <button type="button" className="sp-btn-primary perm-btn-danger" onClick={() => run(() => deleteRole(role.key))} disabled={busy}>{busy ? 'Deleting…' : 'Delete role'}</button>
    </>);
  } else if (confirm === 'reset') {
    footer = (<>
      <button type="button" className="sp-btn-secondary" onClick={() => setConfirm(null)} disabled={busy}>Cancel</button>
      <button type="button" className="sp-btn-primary" onClick={() => run(() => resetRole(role.key))} disabled={busy}>{busy ? 'Resetting…' : 'Reset role'}</button>
    </>);
  } else if (locked) {
    footer = <button type="button" className="sp-btn-primary" onClick={onClose}>Done</button>;
  } else {
    footer = (<>
      <button type="button" className="sp-btn-secondary" onClick={onClose}>Cancel</button>
      <button type="button" className="sp-btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : role.isNew ? 'Create role' : 'Save role'}</button>
    </>);
  }

  // The confirm sits by the buttons (the panel is usually scrolled down to
  // them), and scrolls itself into view.
  const confirmBox = confirm && (
    <div className="perm-confirm" ref={(el) => el && el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })}>
      {confirm === 'delete' ? (<>
        <p><b>Delete the {role.name} role?</b></p>
        <p>{role.people ? `${role.people} ${role.people === 1 ? 'person has' : 'people have'} it. They'll go back to their default role: Standard for employed, Basic for self-employed.` : 'Nobody has it at the moment.'}</p>
      </>) : (<>
        <p><b>Reset {role.name} to how Beu sets it up?</b></p>
        <p>Its name, description and what it can do go back to the original.{role.people ? ` This changes access for the ${role.people} ${role.people === 1 ? 'person' : 'people'} on it.` : ''}</p>
      </>)}
    </div>
  );

  return (
    <SlidePanel title={title} onClose={onClose} width={480} className="ml-panel perm-panel" footer={footer}>
      {role.owner && <p className="perm-intro">{role.description} This role can't be changed or given to anyone else: it belongs to the account holder.</p>}
      {role.frontDesk && <p className="perm-intro">What a device locked in reception mode can do. It's never given to a person, and a locked device never gets more than whoever is signed in on it.</p>}
      {!role.owner && outOfReach && canManage && <p className="perm-intro">This role can do things you can't, so only the owner can change it.</p>}
      {!role.owner && !canManage && <p className="perm-intro">{groupLocked ? 'Roles are shared across your locations, so only the group owner can change them.' : 'Only people who can change access can edit roles.'}</p>}
      {!locked && (
        <>
          <div className="settings-form-group">
            <label className="settings-label" htmlFor="perm-role-name">Role name</label>
            <input id="perm-role-name" type="text" className="settings-input" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} maxLength={40} placeholder="e.g. Senior stylist" autoFocus={!!role.isNew} />
          </div>
          <div className="settings-form-group">
            <label className="settings-label" htmlFor="perm-role-desc">Description</label>
            <input id="perm-role-desc" type="text" className="settings-input" value={v.description || ''} onChange={(e) => setV({ ...v, description: e.target.value })} maxLength={120} placeholder="One line on what this role is for" />
          </div>
        </>
      )}
      <div className="settings-form-group">
        <label className="settings-label">What this role can do</label>
        <div className="ml-panel-list perm-caps">
          {groups.map((g) => (
            <div key={g.id} className="perm-cap-group">
              <p className="perm-cap-group-name">{g.name}</p>
              {g.caps.map((c) => {
                const s = capState(c.id);
                return (
                  <button key={c.id} type="button" className={`perm-cap${s.on ? ' is-on' : ''}${s.disabled ? ' is-locked' : ''}`} role="checkbox" aria-checked={s.on} aria-disabled={s.disabled} onClick={() => toggle(c.id)}>
                    <span className="perm-cap-text"><span>{c.label}</span>{s.note ? <small className="perm-cap-note">{s.note}</small> : c.help && <small>{c.help}</small>}</span>
                    <span className="ml-site-pick-box" aria-hidden="true">
                      {s.on && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {confirmBox}
      {error && <p className="acc-note perm-error">{error}</p>}
      {!locked && !confirm && role.custom && !role.isNew && <button type="button" className="perm-delete" onClick={() => setConfirm('delete')}>Delete this role</button>}
      {!locked && !confirm && role.builtIn && role.edited && <button type="button" className="perm-delete perm-reset" onClick={() => setConfirm('reset')}>Reset to Beu's default</button>}
    </SlidePanel>
  );
}
